import React, { useEffect, useState } from "react";
import { Typography, Select, Button, Card, Divider } from "antd";
import { FilterOutlined, DownOutlined } from "@ant-design/icons";
import LeadCard from "./LeadCard";
import PriorityLeadCard from "./PriorityLeadCard";
import StatisticsChart from "./StatisticsChart";
import { useLocation, useNavigate } from "react-router-dom";
import { connect, useDispatch, useSelector } from "react-redux";
import { fetchAdminAllLeads } from "../../store/action/admin/NewDashboardAction";
import "./newDashboard.css";
import * as type from "../../store/types";
import useLeadOptions from "../../components/NewDashboard/UseLeadOptions";

const { Title, Text } = Typography;

export default function AfterLoginMobile() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedLead, setSelectedLead] = useState(null);
  const [cartLeads, setCartLeads] = useState([]);
  const [subCategoryFilter, setSubCategoryFilter] = useState(null);

  const dispatch = useDispatch();

  const apiLeads = useSelector((state) => state.adminAllLeads.response);
  useEffect(() => {
    dispatch(fetchAdminAllLeads());
  }, [dispatch]);

  const leads = Array.isArray(apiLeads)
    ? apiLeads.map((lead) => ({
        id: lead._id,
        name: lead.name,
        place: lead.location,
        price: lead.budget,
        landType: lead.subcategory?.label || "",
        rating: `${lead.timesBought || 0}/${lead.leadAvailableCount || 0}`,
        status: lead.status || "",
        lookingFor: lead.property_details || "",
        category: lead.category,
        contact: lead.phone,
        type: lead.leadType,
        sqft: lead.spaceSqft || 0,
        siteVisit: lead.siteVisitDays?.join(", ") || "",
        subCategoryId: lead.subcategory?._id,
        subCategoryLabel: lead.subcategory?.label || "",
        subCategoryName: lead.subcategory?.name || "",
      }))
    : [];

  const [filters, setFilters] = useState({
    location: [],
    landType: [],
    budget: [],
    plotSize: [],
  });

  const {
    subCategoryOptions,
    locationOptions,
    landTypeOptions,
    budgetOptions,
    plotSizeOptions,
  } = useLeadOptions(leads);

  const priorityLead = leads.find((lead) => {
    const [purchased, total] = (lead.rating || "0/0").split("/").map(Number);
    return total - purchased === 1;
  });

  const adminLead = priorityLead || leads[0];

  useEffect(() => {
    if (location.state?.filters) {
      const f = location.state.filters;
      const hasAny =
        f.state ||
        f.district ||
        f.area ||
        f.pincode ||
        f.radius ||
        (f.landType && f.landType.length > 0) ||
        (f.plotSize && f.plotSize.length > 0) ||
        (f.budget && f.budget.length > 0);

      if (hasAny) {
        setFilters(f);
      } else {
        // reset if nothing selected
        setFilters({
          location: null,
          landType: null,
          budget: null,
          plotSize: null,
        });
      }
    }
  }, [location.state]);

  const filteredLeads = leads.filter((lead) => {
    return (
      (!filters.location ||
        filters.location.length === 0 ||
        filters.location.includes(lead.place)) &&
      (!filters.landType ||
        filters.landType.length === 0 ||
        filters.landType.includes(lead.landType)) &&
      (!filters.budget ||
        filters.budget.length === 0 ||
        filters.budget.some((b) => Number(lead.price) <= Number(b))) &&
      (!filters.plotSize ||
        filters.plotSize.length === 0 ||
        filters.plotSize.some((s) => Number(lead.plotSize) >= Number(s)))
    );
  });

  const updateLeadStatus = (leadId, newStatus) => {
    dispatch({
      type: type.UPDATE_LEAD,
      payload: {
        id: leadId,
        payload: {
          status: newStatus,
        },
      },
    });

    // Optimistic UI update
    if (selectedLead?.id === leadId) {
      setSelectedLead((prev) => ({
        ...prev,
        status: newStatus,
      }));
    }
  };

  const handleLeadCheck = (lead, checked) => {
    setCartLeads((prev) => {
      if (checked) {
        if (prev.some((l) => l.id === lead.id)) return prev;
        return [...prev, lead];
      }
      return prev.filter((l) => l.id !== lead.id);
    });
  };

  const handleFilter = () => {
    navigate("/admin/dashboard-filter");
  };

  useEffect(() => {
    if (!subCategoryFilter && subCategoryOptions.length > 0) {
      setSubCategoryFilter(subCategoryOptions[0].value); // value is lowercase
    }
  }, [subCategoryOptions, subCategoryFilter]);

  const groupedBySubCategory = React.useMemo(() => {
    if (!Array.isArray(leads) || leads.length === 0) return {};

    return leads.reduce((acc, lead) => {
      const key = lead.subCategoryLabel || "Others";

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(lead);
      return acc;
    }, {});
  }, [leads]);

  const filteredLeadsBySubCategory = React.useMemo(() => {
    if (!subCategoryFilter) return [];
    return filteredLeads.filter(
      (l) => l.subCategoryName?.toLowerCase() === subCategoryFilter
    );
  }, [filteredLeads, subCategoryFilter]);

  return (
    <div style={{ padding: 0, paddingBottom: 80 }}>
      {/* HEADER */}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Title level={4}>Search Lead</Title>
        <FilterOutlined onClick={handleFilter} style={{ fontSize: 20 }} />
      </div>

      {/* FILTER BOX */}
      <Card bordered style={{ borderColor: "#98c3ffff", marginBottom: 12 }}>
        {/* ROW 1 */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1px 1fr" }}>
          <Select
            mode="multiple"
            placeholder="Location"
            bordered={false}
            value={filters.location}
            onChange={(value) => setFilters({ ...filters, location: value })}
            options={locationOptions}
          />
          <div style={{ width: 1, background: "#d9d9d9" }} />
          <Select
            mode="multiple"
            placeholder="Land Type"
            bordered={false}
            value={filters.landType}
            onChange={(value) => setFilters({ ...filters, landType: value })}
            options={landTypeOptions}
          />
        </div>

        <Divider style={{ margin: "8px 0" }} />

        {/* ROW 2 */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1px 1fr" }}>
          <Select
            mode="multiple"
            placeholder="Budget Range"
            bordered={false}
            value={filters.budget}
            onChange={(value) => setFilters({ ...filters, budget: value })}
            options={budgetOptions}
          />
          <div style={{ width: 1, background: "#d9d9d9" }} />
          <Select
            mode="multiple"
            placeholder="Plot Size"
            bordered={false}
            value={filters.plotSize}
            onChange={(value) => setFilters({ ...filters, plotSize: value })}
            options={plotSizeOptions}
          />
        </div>
      </Card>

      <Button type="primary" block style={{ marginBottom: 20, marginTop: 10 }}>
        Search
      </Button>

      {/* PRIORITY LEAD */}
      {adminLead && (
        <PriorityLeadCard
          name={adminLead.name}
          place={adminLead.place}
          rating={adminLead.rating}
        />
      )}

      {/* ALL LEADS */}
      <Title level={4} style={{ marginTop: 16 }}>
        All Leads
      </Title>

      <div
        style={{
          border: "1px solid #ddd",
          borderRadius: 6,
          background: "#fff",
          padding: 6,
        }}
      >
        {filteredLeads.map((lead) => (
          <div
            key={lead.id}
            style={{ width: "100%", marginBottom: 5, marginTop: 5 }}
          >
            <LeadCard
              {...lead}
              showCheckbox
              checked={cartLeads.some((l) => l.id === lead.id)}
              onCheck={(id, checked) => handleLeadCheck(lead, checked)}
              onClick={() =>
                setSelectedLead(selectedLead?.id === lead.id ? null : lead)
              }
            />

            {/* INLINE DETAILS */}
            {selectedLead?.id === lead.id && (
              <Card style={{ background: "#fafafa", marginTop: 8 }}>
                {/* HEADER */}
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Text strong>Lead Details</Text>

                  <Select
                    className="custom-select"
                    placeholder="Update Subcategory"
                    bordered={false}
                    style={{
                      border: "1px solid #DBEAFF",
                      borderRadius: 6,
                      background: "#fff",
                      width: "55%",
                      minWidth: 140,
                    }}
                    value={selectedLead?.subCategoryLabel}
                    options={subCategoryOptions}
                    onChange={(selectedValue) => {
                      const selectedOption = subCategoryOptions.find(
                        (opt) => opt.value === selectedValue
                      );

                      if (!selectedOption) return;

                      // 🔥 API call (same as web)
                      dispatch({
                        type: type.UPDATE_LEAD,
                        payload: {
                          id: selectedLead.id,
                          payload: {
                            subcategory: selectedValue,
                          },
                        },
                      });

                      // ⚡ Optimistic UI update
                      setSelectedLead((prev) => ({
                        ...prev,
                        subCategoryLabel: selectedValue,
                        subCategoryName: selectedValue,
                      }));
                    }}
                  />
                </div>

                <Divider style={{ margin: "8px 0" }} />

                {/* GRID ROW 1 */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1px 1fr",
                    alignItems: "center",
                  }}
                >
                  <div style={{ padding: 6 }}>
                    <strong>Name</strong>
                    <div>{lead.name}</div>
                  </div>

                  <div
                    style={{ width: 1, height: "100%", background: "#DBEAFF" }}
                  />

                  <div style={{ padding: 6 }}>
                    <strong>Looking For</strong>
                    <div>{lead.lookingFor}</div>
                  </div>
                </div>

                <Divider style={{ margin: "6px 0" }} />

                {/* GRID ROW 2 */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1px 1fr",
                    alignItems: "center",
                  }}
                >
                  <div style={{ padding: 6 }}>
                    <strong>Sq.ft</strong>
                    <div>{lead.sqft}</div>
                  </div>

                  <div
                    style={{ width: 1, height: "100%", background: "#DBEAFF" }}
                  />

                  <div style={{ padding: 6 }}>
                    <strong>Location</strong>
                    <div>{lead.place}</div>
                  </div>
                </div>

                <Divider style={{ margin: "6px 0" }} />

                {/* GRID ROW 3 */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1px 1fr",
                    alignItems: "center",
                  }}
                >
                  <div style={{ padding: 6 }}>
                    <strong>Contact</strong>
                    <div>{lead.contact}</div>
                  </div>

                  <div
                    style={{ width: 1, height: "100%", background: "#DBEAFF" }}
                  />

                  <div style={{ padding: 6 }}>
                    <strong>Budget</strong>
                    <div>{lead.budget}</div>
                  </div>
                </div>

                <Divider style={{ margin: "6px 0" }} />

                {/* GRID ROW 4 */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1px 1fr",
                    alignItems: "center",
                  }}
                >
                  <div style={{ padding: 6 }}>
                    <strong>Build / Invest</strong>
                    <div>{lead.type}</div>
                  </div>

                  <div
                    style={{ width: 1, height: "100%", background: "#DBEAFF" }}
                  />

                  <div style={{ padding: 6 }}>
                    <strong>Site Visit</strong>
                    <div>{lead.siteVisit}</div>
                  </div>
                </div>

                <Button type="primary" block style={{ marginTop: 14 }}>
                  Buy Now
                </Button>
              </Card>
            )}
          </div>
        ))}
      </div>

      {/* LEAD STATISTICS */}
      <Title level={5} style={{ marginTop: 16 }}>
        Lead Statistics
      </Title>

      <div style={{ overflowX: "auto" }}>
        <Card style={{ background: "#fff" }}>
          <StatisticsChart />
        </Card>
      </div>

      {/* STATUS FILTER */}
      {/* SUBCATEGORY FILTER */}
      <div style={{ marginTop: 16 }}>
        <Select
          value={subCategoryFilter}
          bordered={false}
          suffixIcon={<DownOutlined />}
          onChange={setSubCategoryFilter}
          options={subCategoryOptions}
          style={{ width: "100%" }}
        />
      </div>

      <Card bordered style={{ marginTop: 8 }}>
        {filteredLeadsBySubCategory.map((lead) => (
          <LeadCard key={lead.id} {...lead} showCheckbox={false} />
        ))}
      </Card>

      {/* CART BAR */}
      {cartLeads.length > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            background: "#fff",
            borderTop: "1px solid #ddd",
            padding: 12,
            display: "flex",
            justifyContent: "space-between",
            zIndex: 1000,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ color: "#007bff", fontSize: 16, fontWeight: 600 }}>
              No. of {cartLeads.length === 1 ? "lead" : "leads"} Selected:{" "}
              {cartLeads.length}
            </span>
            <span style={{ color: "#555", fontSize: 13 }}>
              * Lead amount may change while someone purchases the same lead at
              a time
            </span>
          </div>
          <Button
            type="primary"
            onClick={() => navigate("/admin/my-cart", { state: { cartLeads } })}
          >
            Add to Cart
          </Button>
        </div>
      )}
    </div>
  );
}
